import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Crown,
  Key,
  ArrowRight,
  Check,
  X,
  Shield,
  Sparkles,
  Gift,
  Zap,
  Star,
  Users,
  TrendingUp,
  DollarSign,
  BarChart3,
  Headphones,
  MessageCircle,
  Compass,
  Award,
} from 'lucide-react';
import api from '../services/api';
import './VIPCenter.css';

const OTP_LENGTH = 6;

const STATS = [
  { label: 'VIP Members',    value: '7,487',  icon: Users },
  { label: 'Win Rate',       value: '71%',    icon: TrendingUp },
  { label: 'Total Profit',   value: '$50M+',  icon: DollarSign },
  { label: 'Signals / mo',   value: '1,200+', icon: BarChart3 },
];

const TIERS = [
  {
    name: 'Entry Level Tier',
    threshold: '>=\u200B$100,000',
    perks: ['Standard Market Analysis', 'Community Access', 'Standard Support'],
  },
  {
    name: 'Intermediate Tier',
    threshold: '>=\u200B$300,000',
    perks: ['Real time Market Analysis Priority Support', 'VIP Community Access', 'Trading Strategy Sharing'],
  },
  {
    name: 'Advanced Tier',
    threshold: '>=\u200B$500,000',
    perks: ['Personal Trading Advisor', 'Custom Strategy Development', 'Early Access to New Features', 'Exclusive Trading Events'],
  },
  {
    name: 'Elite Tier',
    threshold: '>=\u200B$1,000,000',
    perks: ['24/7 Dedicated Trading Advisor', 'AI Quantitative Strategy', 'Global Finance Summit Invitation', 'Exclusive Investment Opportunities', 'One-on-One Trading Guidance'],
  },
  {
    name: 'Core Tier',
    threshold: '>=\u200B$2,000,000',
    perks: ['All Privileges', 'Join Gary Strategy Club and attend the shareholders\' meeting', 'Quarterly student networking events for resource sharing'],
  },
];

const BENEFITS = [
  { icon: Zap,           title: 'Real-time Signals',   desc: 'Trading signals pushed the instant our desk opens a position.' },
  { icon: Star,          title: '1-on-1 Coaching',     desc: 'Direct mentorship sessions with senior analysts on your setups.' },
  { icon: BarChart3,     title: 'Exclusive Research',  desc: 'Institutional-grade macro briefs, sector rotations, focus tickers.' },
  { icon: MessageCircle, title: 'Private Discord',     desc: 'VIP-only community, live rooms, and rapid alert channels.' },
  { icon: Headphones,    title: 'Priority Support',    desc: 'Dedicated concierge — skip the queue on all VIP requests.' },
  { icon: Compass,       title: 'Weekly Strategy',     desc: 'Weekly calls covering week ahead, risk map, and focus tickers.' },
];

const VIPCenter = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isVip, user, refreshUser } = useAuth();

  const [showActivationModal, setShowActivationModal] = useState(false);
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [activationError, setActivationError] = useState('');
  const [activationSuccess, setActivationSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showActivatingOverlay, setShowActivatingOverlay] = useState(false);
  const [activationProgress, setActivationProgress] = useState(0);
  const otpRefs = useRef([]);

  const otpString = otp.join('');

  const handleOtpChange = (idx, v) => {
    if (!/^[a-zA-Z0-9]?$/.test(v)) return;
    const next = [...otp];
    next[idx] = v.toUpperCase();
    setOtp(next);
    setActivationError('');
    if (v && idx < OTP_LENGTH - 1) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKey = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) otpRefs.current[idx - 1]?.focus();
    if (e.key === 'Enter' && otpString.length === OTP_LENGTH) handleActivation();
  };

  const handleOtpPaste = (e) => {
    const text = (e.clipboardData?.getData('text') || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, OTP_LENGTH).toUpperCase();
    if (!text) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < text.length; i += 1) next[i] = text[i];
    setOtp(next);
    otpRefs.current[Math.min(text.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleActivation = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/vip' } });
      return;
    }
    if (otpString.length !== OTP_LENGTH) {
      setActivationError('Please enter the 6-character code');
      return;
    }
    setLoading(true);
    setActivationError('');

    try {
      const response = await api.post('/vip/activate', { code: otpString });
      if (response.data.success) {
        setShowActivationModal(false);
        setShowActivatingOverlay(true);
        setActivationProgress(0);

        const progressInterval = setInterval(() => {
          setActivationProgress((prev) => (prev >= 100 ? 100 : prev + 2));
        }, 55);

        setTimeout(async () => {
          clearInterval(progressInterval);
          setActivationProgress(100);
          if (refreshUser) await refreshUser();
          setTimeout(() => {
            setActivationSuccess(true);
            setLoading(false);
          }, 300);
          setTimeout(() => {
            setShowActivatingOverlay(false);
            setActivationSuccess(false);
            setOtp(Array(OTP_LENGTH).fill(''));
          }, 4500);
        }, 2900);
      }
    } catch (error) {
      setActivationError(error.response?.data?.message || 'Activation failed, please check your code');
      setLoading(false);
    }
  };

  const openActivationModal = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/vip' } });
      return;
    }
    setShowActivationModal(true);
    setOtp(Array(OTP_LENGTH).fill(''));
    setActivationError('');
    setActivationSuccess(false);
    setTimeout(() => otpRefs.current[0]?.focus(), 60);
  };

  const backgroundFx = (
    <div className="vip-page__bg" aria-hidden="true">
      <div className="vip-page__fx-grid" />
      <div className="vip-page__fx-wash" />
      <div className="vip-page__fx-blob vip-page__fx-blob--a" />
      <div className="vip-page__fx-blob vip-page__fx-blob--b" />
      <div className="vip-page__fx-blob vip-page__fx-blob--c" />
      <div className="vip-page__fx-ring" />
      <div className="vip-page__fx-beam vip-page__fx-beam--1" />
      <div className="vip-page__fx-beam vip-page__fx-beam--2" />
      <div className="vip-page__fx-dots">
        {Array.from({ length: 24 }).map((_, i) => (
          <span
            key={i}
            className="vip-page__fx-dot"
            style={{
              '--i': i,
              left: `${5 + ((i * 41) % 90)}%`,
              animationDelay: `${(i % 11) * 0.4}s`,
              animationDuration: `${9 + (i % 6) * 1.3}s`,
            }}
          />
        ))}
      </div>
    </div>
  );

  const Seal = ({ size = 168, dim, icon }) => (
    <div className={`vipk-seal ${dim ? 'vipk-seal--dim' : ''}`} style={{ width: size, height: size }}>
      <span className="vipk-seal__ring vipk-seal__ring--a" />
      <span className="vipk-seal__ring vipk-seal__ring--b" />
      <span className="vipk-seal__ring vipk-seal__ring--c" />
      <span className="vipk-seal__glow" />
      <div className="vipk-seal__core">
        {icon}
      </div>
    </div>
  );

  // ============ ALREADY VIP ============
  if (isVip) {
    return (
      <div className="vip-page vip-page--v4 vip-page--crowned premium-page">
        {backgroundFx}
        <div className="vipk-container">
          <section className="vipk-hero vipk-hero--crowned">
            <div className="vipk-hero__eyebrow">
              <Sparkles size={13} />
              <span>You are a Lifetime VIP</span>
            </div>

            <Seal size={190} icon={<Crown size={62} strokeWidth={1.5} />} />

            <h1 className="vipk-hero__title">
              Welcome back, <span className="vipk-gold-text">{user?.username || 'VIP Member'}</span>
            </h1>
            <p className="vipk-hero__sub">
              All premium capabilities are unlocked. Enjoy exclusive research,
              signals, coaching, and priority services curated by our senior desk.
            </p>

            <div className="vipk-hero__badge">
              <Award size={18} />
              <span>Lifetime Access · Activated</span>
            </div>
          </section>

          <section className="vipk-benefits">
            <div className="vipk-section__head">
              <h2 className="vipk-section__title">
                <Crown size={20} /> Your VIP capabilities
              </h2>
              <p className="vipk-section__sub">Every module below is fully unlocked.</p>
            </div>

            <div className="vipk-benefits__grid">
              {BENEFITS.map((b, i) => {
                const Ico = b.icon;
                return (
                  <article key={i} className="vipk-benefit vipk-benefit--unlocked" style={{ animationDelay: `${0.08 + i * 0.05}s` }}>
                    <div className="vipk-benefit__icon">
                      <Ico size={22} />
                    </div>
                    <h3 className="vipk-benefit__title">{b.title}</h3>
                    <p className="vipk-benefit__desc">{b.desc}</p>
                    <span className="vipk-benefit__check" aria-hidden="true">
                      <Check size={12} strokeWidth={3} />
                    </span>
                  </article>
                );
              })}
            </div>
          </section>

          <div className="vipk-footer-actions">
            <button className="vipk-cta vipk-cta--gold" onClick={() => navigate('/')}>
              <span className="vipk-cta__shine" aria-hidden="true" />
              Start Exploring
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============ NOT VIP ============
  return (
    <div className="vip-page vip-page--v4 premium-page">
      {backgroundFx}

      <div className="vipk-container">
        {/* HERO */}
        <section className="vipk-hero">
          <div className="vipk-hero__eyebrow">
            <Sparkles size={13} />
            <span>Exclusive Invitation</span>
          </div>

          <Seal
            size={168}
            icon={<Crown size={54} strokeWidth={1.5} />}
          />

          <h1 className="vipk-hero__title">
            Activate <span className="vipk-gold-text">Lifetime VIP</span>
          </h1>
          <p className="vipk-hero__sub">
            Unlock institutional-grade trading strategies, private research,
            and a senior mentor desk — permanently, with a single activation code.
          </p>

          <div className="vipk-hero__stats">
            {STATS.map((s, i) => {
              const Ico = s.icon;
              return (
                <div key={i} className="vipk-stat" style={{ animationDelay: `${0.15 + i * 0.08}s` }}>
                  <Ico size={16} className="vipk-stat__icon" />
                  <span className="vipk-stat__value">{s.value}</span>
                  <span className="vipk-stat__label">{s.label}</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* MEMBERSHIP TIERS — only visible to logged-in users */}
        {isAuthenticated && (
          <section className="vipk-tiers">
            <div className="vipk-section__head">
              <p className="vipk-section__eyebrow">
                <Crown size={12} /> Membership Tiers
              </p>
              <h2 className="vipk-section__title">Investment Levels</h2>
              <p className="vipk-section__sub">
                Higher tiers unlock exclusive privileges and personalized services.
              </p>
            </div>
            <div className="vipk-tiers__grid">
              {TIERS.map((t, i) => (
                <article
                  key={i}
                  className={`vipk-tier-card ${i === TIERS.length - 1 ? 'vipk-tier-card--top' : ''}`}
                  style={{ animationDelay: `${0.1 + i * 0.08}s` }}
                >
                  <div className="vipk-tier-card__star">
                    <Star size={20} />
                  </div>
                  <h3 className="vipk-tier-card__name">{t.name}</h3>
                  <span className="vipk-tier-card__threshold">{t.threshold}</span>
                  <ul className="vipk-tier-card__perks">
                    {t.perks.map((p, j) => (
                      <li key={j}>
                        <Check size={13} strokeWidth={2.5} />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* MAIN ACTIVATE CARD (single, elegant, gold-bordered light card) */}
        <section className="vipk-activate">
          <div className="vipk-activate__frame">
            <div className="vipk-activate__corner vipk-activate__corner--tl" />
            <div className="vipk-activate__corner vipk-activate__corner--tr" />
            <div className="vipk-activate__corner vipk-activate__corner--bl" />
            <div className="vipk-activate__corner vipk-activate__corner--br" />

            <div className="vipk-activate__glow" aria-hidden="true" />
            <div className="vipk-activate__damask" aria-hidden="true" />

            {/* Ribbon badge floating on top edge */}
            <div className="vipk-activate__ribbon" aria-hidden="true">
              <span className="vipk-activate__ribbon-tail vipk-activate__ribbon-tail--l" />
              <span className="vipk-activate__ribbon-body">
                <Crown size={12} strokeWidth={2.4} />
                <span>VIP · LIFETIME · EXCLUSIVE</span>
                <Crown size={12} strokeWidth={2.4} />
              </span>
              <span className="vipk-activate__ribbon-tail vipk-activate__ribbon-tail--r" />
            </div>

            {/* Floating dust particles inside the card */}
            <div className="vipk-activate__dust" aria-hidden="true">
              {Array.from({ length: 14 }).map((_, i) => (
                <span
                  key={i}
                  className="vipk-activate__dust-dot"
                  style={{
                    '--i': i,
                    left: `${5 + ((i * 37) % 90)}%`,
                    top: `${20 + ((i * 53) % 65)}%`,
                    animationDelay: `${(i % 8) * 0.6}s`,
                    animationDuration: `${5 + (i % 4) * 1.4}s`,
                  }}
                />
              ))}
            </div>

            <div className="vipk-activate__inner">
              <p className="vipk-activate__eyebrow">
                <Key size={13} />
                <span>Redeem your activation code</span>
              </p>

              <h2 className="vipk-activate__title">
                Enter your <span className="vipk-gold-text">Lifetime VIP</span> code
              </h2>

              <p className="vipk-activate__desc">
                All <strong>John Baker Beverly WhatsApp group</strong> members
                can freely contact private assistant <strong>Emily</strong> to obtain
                a <em>lifetime VIP activation code</em>.
              </p>

              {/* Decorative ornament divider */}
              <div className="vipk-ornament" aria-hidden="true">
                <span className="vipk-ornament__line" />
                <span className="vipk-ornament__diamond">
                  <Crown size={14} strokeWidth={2.2} />
                </span>
                <span className="vipk-ornament__line" />
              </div>

              <ul className="vipk-activate__perks">
                <li>
                  <span className="vipk-activate__perk-icon">
                    <Gift size={14} />
                  </span>
                  <span>Permanent unlock · zero recurring fees</span>
                  <ArrowRight size={13} className="vipk-activate__perk-arrow" />
                </li>
                <li>
                  <span className="vipk-activate__perk-icon">
                    <Shield size={14} />
                  </span>
                  <span>Full access to VIP-only rooms and analyses</span>
                  <ArrowRight size={13} className="vipk-activate__perk-arrow" />
                </li>
                <li>
                  <span className="vipk-activate__perk-icon">
                    <Zap size={14} />
                  </span>
                  <span>Instant activation once code is verified</span>
                  <ArrowRight size={13} className="vipk-activate__perk-arrow" />
                </li>
              </ul>

              <div className="vipk-activate__cta-wrap">
                <span className="vipk-activate__cta-halo" aria-hidden="true" />
                <button type="button" className="vipk-cta vipk-cta--gold vipk-cta--lg" onClick={openActivationModal}>
                  <span className="vipk-cta__shine" aria-hidden="true" />
                  <Key size={18} />
                  <span>Enter Activation Code</span>
                  <ArrowRight size={18} />
                </button>
              </div>

              <p className="vipk-activate__hint">
                <Sparkles size={11} />
                <span>Need a code? Message assistant <strong>Emily</strong> on WhatsApp.</span>
              </p>
            </div>
          </div>
        </section>

        {/* BENEFITS GRID */}
        <section className="vipk-benefits">
          <div className="vipk-section__head">
            <p className="vipk-section__eyebrow">
              <Crown size={12} /> Member Benefits
            </p>
            <h2 className="vipk-section__title">What VIP unlocks</h2>
            <p className="vipk-section__sub">
              Every capability our senior desk uses — handed to you, for life.
            </p>
          </div>

          <div className="vipk-benefits__grid">
            {BENEFITS.map((b, i) => {
              const Ico = b.icon;
              return (
                <article key={i} className="vipk-benefit" style={{ animationDelay: `${0.1 + i * 0.06}s` }}>
                  <div className="vipk-benefit__icon">
                    <Ico size={22} />
                  </div>
                  <h3 className="vipk-benefit__title">{b.title}</h3>
                  <p className="vipk-benefit__desc">{b.desc}</p>
                </article>
              );
            })}
          </div>
        </section>

        {/* FOOTER CTA */}
        <section className="vipk-footcta">
          <div className="vipk-footcta__seal">
            <Crown size={26} strokeWidth={1.6} />
          </div>
          <div className="vipk-footcta__text">
            <p className="vipk-footcta__eyebrow">
              <Sparkles size={12} /> Ready to join
            </p>
            <h3 className="vipk-footcta__title">Activate your lifetime desk pass</h3>
          </div>
          <button type="button" className="vipk-cta vipk-cta--gold" onClick={openActivationModal}>
            <span className="vipk-cta__shine" aria-hidden="true" />
            <Key size={16} />
            Activate Now
            <ArrowRight size={16} />
          </button>
        </section>
      </div>

      {/* ============ ACTIVATION MODAL ============ */}
      {showActivationModal && (
        <div className="vipk-modal-overlay" onClick={() => setShowActivationModal(false)}>
          <div className="vipk-modal" onClick={(e) => e.stopPropagation()}>
            <button className="vipk-modal__close" onClick={() => setShowActivationModal(false)}>
              <X size={18} />
            </button>

            <div className="vipk-modal__glow" aria-hidden="true" />

            <div className="vipk-modal__head">
              <div className="vipk-modal__icon">
                <Key size={24} />
              </div>
              <h2>Enter Activation Code</h2>
              <p>
                Enter the 6-character lifetime VIP code you received from
                assistant <strong>Emily</strong>.
              </p>
            </div>

            <div className="vipk-modal__body">
              <div className="vipk-otp">
                {otp.map((v, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    className={`vipk-otp__slot ${v ? 'is-filled' : ''}`}
                    type="text"
                    inputMode="text"
                    maxLength={1}
                    value={v}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKey(i, e)}
                    onPaste={handleOtpPaste}
                    onClick={() => otpRefs.current[i]?.select()}
                    autoComplete="off"
                  />
                ))}
              </div>

              {activationError && (
                <div className="vipk-modal__error">
                  <X size={14} />
                  <span>{activationError}</span>
                </div>
              )}
            </div>

            <div className="vipk-modal__foot">
              <button
                className="vipk-modal__btn vipk-modal__btn--ghost"
                onClick={() => setShowActivationModal(false)}
              >
                Cancel
              </button>
              <button
                className="vipk-modal__btn vipk-modal__btn--gold"
                onClick={handleActivation}
                disabled={loading || otpString.length !== OTP_LENGTH}
              >
                {loading ? (
                  'Verifying…'
                ) : (
                  <>
                    <Sparkles size={16} />
                    Activate Now
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ ACTIVATION CEREMONY (fullscreen) ============ */}
      {showActivatingOverlay && (
        <div className={`vipk-ceremony ${activationSuccess ? 'vipk-ceremony--done' : ''}`}>
          <div className="vipk-ceremony__bg" />

          {/* Radial gold rays behind the seal */}
          <div className="vipk-ceremony__rays" aria-hidden="true">
            {Array.from({ length: 12 }).map((_, i) => (
              <span
                key={i}
                className="vipk-ceremony__ray"
                style={{ '--r': `${i * 30}deg` }}
              />
            ))}
          </div>

          {/* Halo pulses */}
          <div className="vipk-ceremony__halo vipk-ceremony__halo--1" />
          <div className="vipk-ceremony__halo vipk-ceremony__halo--2" />
          <div className="vipk-ceremony__halo vipk-ceremony__halo--3" />

          {/* Gold particles bursting from center */}
          <div className="vipk-ceremony__spark" aria-hidden="true">
            {Array.from({ length: 40 }).map((_, i) => (
              <span
                key={i}
                className="vipk-ceremony__spark-dot"
                style={{
                  '--angle': `${(i / 40) * 360}deg`,
                  '--delay': `${(i % 10) * 0.05}s`,
                  '--dist': `${180 + (i % 6) * 45}px`,
                }}
              />
            ))}
          </div>

          <div className="vipk-ceremony__center">
            {!activationSuccess ? (
              <>
                <div className="vipk-ceremony__seal">
                  <span className="vipk-ceremony__seal-ring vipk-ceremony__seal-ring--a" />
                  <span className="vipk-ceremony__seal-ring vipk-ceremony__seal-ring--b" />
                  <span className="vipk-ceremony__seal-ring vipk-ceremony__seal-ring--c" />
                  <div className="vipk-ceremony__seal-core">
                    <Crown size={72} strokeWidth={1.4} />
                  </div>
                </div>

                <div className="vipk-ceremony__progress">
                  <svg viewBox="0 0 120 120">
                    <defs>
                      <linearGradient id="vipkGoldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#fffbeb" />
                        <stop offset="40%" stopColor="#f0d78c" />
                        <stop offset="80%" stopColor="#d4af37" />
                        <stop offset="100%" stopColor="#b8860b" />
                      </linearGradient>
                    </defs>
                    <circle
                      className="vipk-ceremony__progress-track"
                      cx="60" cy="60" r="54"
                    />
                    <circle
                      className="vipk-ceremony__progress-bar"
                      cx="60" cy="60" r="54"
                      style={{
                        strokeDasharray: 2 * Math.PI * 54,
                        strokeDashoffset: 2 * Math.PI * 54 * (1 - activationProgress / 100),
                      }}
                    />
                  </svg>
                  <span className="vipk-ceremony__progress-value">{activationProgress}%</span>
                </div>

                <h2 className="vipk-ceremony__title">
                  <Sparkles size={22} />
                  Activating VIP Membership
                </h2>
                <p className="vipk-ceremony__sub">
                  Unlocking your lifetime access, please wait…
                </p>

                <div className="vipk-ceremony__dots">
                  <span></span><span></span><span></span>
                </div>
              </>
            ) : (
              <>
                <div className="vipk-ceremony__success">
                  <div className="vipk-ceremony__success-crown">
                    <Crown size={72} strokeWidth={1.4} />
                  </div>
                  <div className="vipk-ceremony__success-check">
                    <Check size={30} strokeWidth={3} />
                  </div>
                </div>

                <h2 className="vipk-ceremony__title vipk-ceremony__title--done">
                  Congratulations!
                </h2>
                <p className="vipk-ceremony__sub">
                  Your Lifetime VIP Membership has been activated.
                </p>

                <div className="vipk-ceremony__badge">
                  <Shield size={18} />
                  <span>Lifetime VIP Member</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VIPCenter;
